import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import styled from '@emotion/styled';
import { Box, Fade, Menu, MenuItem, Typography, popoverClasses, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { SmallAvatar } from '@components/avatar';
import { Button } from '@components/index';
import { RoutingTabs } from '@components/tab';
import { useStore } from '@stores/RootStore';
import { fonts } from '@theme/fontsCustomer';
import TailImg from '@images/tail.png';
import { AdminConsoleIcon, PersonIcon, LogoutIcon, ProfileIcon, TriangleIcon, CheckIcon, GlobeIcon } from '@icons/index';
import localStorage from '@repositories/browserStorage/localStorage/LocalStorage';
import sessionStorage from '@repositories/browserStorage/sessionStorage/SessionStorage';
import { useLogout } from '@queryHooks/useUser';
import { getDomainFromBaseUrl } from '@utils/urlUtils';
import i18n from '@locales/i18n';
import { MobileGnb } from './MobileGnb';
import { MyProfileModal } from './myProfileModal';

const { ENABLED_PRIVACY_POLICY } = getEnvConfig();

const profileStyles = {
  elevation: 0,
  sx: {
    overflow: 'visible',
    boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.06);',
    borderRadius: '3px',
    border: '1px solid #e1e4e8',
    '& .MuiList-root': {
      padding: '6px',
      border: 'none',
      boxShadow: 'none',
      borderRadius: '3px',
    },
    '& .MuiAvatar-root': {
      width: 32,
      height: 32,
      ml: -0.5,
      mr: 1,
    },
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: -6,
      right: 40,
      width: 14,
      height: 7,
      bgcolor: 'background.paper',
      background: `url(${TailImg}) 0 0 no-repeat`,
      backgroundSize: '14px 7px',
      webkitBackgroundSize: '14px 7px',
      zIndex: 0,
    },
  },
};

export const TopGnb = observer(() => {
  const theme = useTheme();
  const {
    authStore,
    uiStore: { profileModalStore: modal },
  } = useStore();
  const { t } = useTranslation();
  const { INTRODUCTION_PAGE, COMPANY_ID } = getEnvConfig();
  const enabledPrivacyPolicy = ENABLED_PRIVACY_POLICY === 'true';

  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const profileMenuOwnerRef = useRef<HTMLDivElement>(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const handleProfileHover = () => {
    setIsProfileMenuOpen(true);
  };

  const handleProfileLeave = () => {
    setIsProfileMenuOpen(false);
    setIsLangOpen(false);
  };

  const { logout } = useLogout();

  const handleClickLoginBtn = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  const handleIntroduceInquiryClick = () => {
    navigate('/tenant-request');
  };

  const handleClickManagerConsole = () => {
    // 새 탭에서 sessionStorage가 공유되지 않으므로 localStorage에 임시 저장
    const tk = sessionStorage.get('tk');
    const authInfo = sessionStorage.get('authInfo');
    if (tk && authInfo) {
      localStorage.set('authTransfer', JSON.stringify({ tk, authInfo }));
    }

    const url = `/manager/${authStore.curTenant.id}/home`;
    window.open(url, '_blank');
  };

  const handleChangeLang = (lang: string) => {
    i18n.changeLanguage(lang);
    dayjs.locale(lang);
    localStorage.set('lang', lang);

    const isSecure = window.location.protocol === 'https:';
    const secureAttr = isSecure ? ' secure;' : '';

    // COMPANY_ID에 따른 도메인 설정
    const domainAttr = getDomainFromBaseUrl();
    const cookieString = `SMP_lang=${lang}; path=/;${secureAttr} ${domainAttr}`;
    document.cookie = cookieString;

    setIsLangOpen(false);
  };

  /**
   * INTRODUCTION_PAGE가 true가 아닌 경우에는 소개 페이지 제거
   */
  const tabList =
    INTRODUCTION_PAGE === 'true'
      ? [
          { name: t('GNB_Menu_01'), path: '/introduction' },
          { name: `${t('Ad_GNB_Menu_01')}`, path: '/store' },
          { name: `MY ${t('Workflow_Create_68')}`, path: '/subscriptions' },
        ]
      : [
          { name: `${t('Ad_GNB_Menu_01')}`, path: '/store' },
          { name: `MY ${t('Workflow_Create_68')}`, path: '/subscriptions' },
        ];

  return (
    <Container>
      {matches ? (
        <>
          <LogoLinkWrap to="/store">
            <img
              src={COMPANY_ID === 'KT_DS' ? '/logo-ktds.png' : '/logo-mobile.png'}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
            />
          </LogoLinkWrap>
          <MobileGnb />
        </>
      ) : (
        <>
          <LogoLinkWrap to="/store">
            <img
              src={COMPANY_ID === 'KT_DS' ? '/logo-ktds.png' : '/logo.png'}
              alt="Logo"
              style={{ width: '100%', height: '100%', objectFit: 'scale-down' }}
            />
          </LogoLinkWrap>
          <MenusWrapper>
            <StyledLink variant="line" tabList={tabList} />
          </MenusWrapper>
          <UserButtonWrapper>
            {authStore.isLogin ? (
              <>
                {enabledPrivacyPolicy && (
                  <PrivacyPolicyLink onClick={() => navigate('/privacy-policy')}>{t('Sign_In_08')}</PrivacyPolicyLink>
                )}
                {authStore.canAccessManagerConsole && authStore.curTenant.id && (
                  <AdminConsoleLink onClick={handleClickManagerConsole}>
                    <AdminConsoleIcon width={16} height={16} />
                    {t('GNB_Menu_06')}
                  </AdminConsoleLink>
                )}
                <DropdownOwner
                  ref={profileMenuOwnerRef}
                  onMouseEnter={handleProfileHover}
                  onMouseLeave={handleProfileLeave}
                >
                  <Profile className={authStore.canAccessManagerConsole ? 'admin-profile' : ''}>
                    <SmallAvatar>
                      <ProfileIcon bgColor={theme.colors['bg-primary']} />
                    </SmallAvatar>
                  </Profile>
                </DropdownOwner>
                <Menu
                  anchorEl={profileMenuOwnerRef.current}
                  open={isProfileMenuOpen}
                  MenuListProps={{
                    onMouseEnter: handleProfileHover,
                    onMouseLeave: handleProfileLeave,
                    sx: { pointerEvents: 'auto' },
                  }}
                  disableAutoFocusItem
                  TransitionComponent={Fade}
                  transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                  PaperProps={profileStyles}
                  sx={{ [`&.${popoverClasses.root}`]: { pointerEvents: 'none', marginTop: '-1px' } }}
                >
                  <StyledMenuItem
                    value="profile"
                    onClick={() => {
                      modal.open();
                      handleProfileLeave();
                    }}
                  >
                    <ProfileMenuWrapper>
                      <PersonIcon width={18} height={18} />
                      <Typography noWrap component="span" variant="body1" color={theme.colors['text-gray-main']}>
                        {t('GNB_Profile_Menu_01')}
                      </Typography>
                    </ProfileMenuWrapper>
                  </StyledMenuItem>
                  <StyledMenuItem value="language">
                    <LanguageWrapper onClick={() => setIsLangOpen(prev => !prev)}>
                      <GlobeIcon width={18} height={18} />
                      <Typography
                        noWrap
                        component="span"
                        variant="body1"
                        color={theme.colors['text-gray-main']}
                        sx={{ marginRight: 'auto' }}
                      >
                        {t('GNB_Profile_Menu_02')}
                      </Typography>
                      <TriangleIcon rotateNum={90} width={16} height={16} color={theme.colors['ic-gray-dark']} />
                      {isLangOpen && (
                        <Languages>
                          <Language onClick={() => handleChangeLang('ko')}>
                            <span className={i18n.language === 'ko' ? 'ko' : undefined}>{t('Language.Korean')}</span>
                            {i18n.language === 'ko' && (
                              <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />
                            )}
                          </Language>
                          <Language onClick={() => handleChangeLang('en')}>
                            <span className={i18n.language === 'en' ? 'en' : undefined}>{t('Language.English')}</span>
                            {i18n.language === 'en' && (
                              <CheckIcon width={20} height={20} color={theme.colors['ic-primary-light']} />
                            )}
                          </Language>
                        </Languages>
                      )}
                    </LanguageWrapper>
                  </StyledMenuItem>
                  <StyledMenuItem
                    value="logout"
                    onClick={() => {
                      handleProfileLeave();
                      logout();
                      navigate('/login');
                    }}
                  >
                    <ProfileMenuWrapper>
                      <LogoutIcon width={18} height={18} />
                      <Typography noWrap component="span" variant="body1" color={theme.colors['text-gray-main']}>
                        {t('GNB_Profile_Menu_03')}
                      </Typography>
                    </ProfileMenuWrapper>
                  </StyledMenuItem>
                </Menu>
              </>
            ) : (
              <LoginWrap>
                {enabledPrivacyPolicy && (
                  <PrivacyPolicyBtn
                    onClick={() => navigate('/privacy-policy')}
                    variant="contain"
                    size="small"
                    paddingHorizontal={16}
                  >
                    {t('Sign_In_08')}
                  </PrivacyPolicyBtn>
                )}
                <IntroductionBtn
                  onClick={handleIntroduceInquiryClick}
                  variant="contain"
                  size="small"
                  paddingHorizontal={16}
                >
                  {t('GNB_Menu_04')}
                </IntroductionBtn>
                <LoginBtn onClick={handleClickLoginBtn} variant="contain" size="small" paddingHorizontal={16}>
                  {t('GNB_Menu_05')}
                </LoginBtn>
              </LoginWrap>
            )}
          </UserButtonWrapper>
        </>
      )}
      {modal.visible && <MyProfileModal />}
    </Container>
  );
});

const Container = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--employee-gnb-height);
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 50px;
  background-color: #fff;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.09);
  flex-shrink: 0;
  gap: 40px;
  z-index: 1001;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    height: var(--mobile-employee-gnb-height);
    padding: 0 26px 0 24px;
    gap: 15px;
  }
`;

const UserButtonWrapper = styled(Box)`
  display: flex;
  align-items: center;
  margin-left: auto;
  height: 100%;
`;

const LogoLinkWrap = styled(Link)`
  width: 148px;
  height: 25px;
  display: inline-block;
  font-weight: 700;
  text-decoration: none;
  ${({ theme }) => theme.breakpoints.between('xxs', 'sm')} {
    width: 139px;
    height: 22px;
    margin-right: auto;
  }
  z-index: 1001;
`;

const MenusWrapper = styled(Box)`
  max-width: 1440px;
  height: 100%;
  display: flex;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
  justify-content: center;
  gap: 32px;
  align-items: center;
`;
const ProfileMenuWrapper = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: center;
`;

const LanguageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  gap: 6px;
`;

const Languages = styled.ul`
  position: absolute;
  right: 202px;
  top: -7px;
  width: 180px;
  height: fit-content;
  padding: 6px;
  border: 1px solid ${props => props.theme.colors['border-gray-lighter']};
  border-radius: 3px;
  background-color: ${props => props.theme.colors['bg-white']};
  z-index: 1001;
`;

const Language = styled.li`
  display: flex;
  flex-direction: row;
  padding: 10px 0px 10px 11px;
  justify-content: space-between;
  align-items: center;
  height: 40px;
  color: ${props => props.theme.colors['text-primary-light']};
  ${fonts.Body2};

  &:hover {
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
  & > span {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
    &.ko,
    &.en {
      color: ${({ theme }) => theme.colors['text-primary-light']};
    }
  }
`;

const LoginWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
`;

const IntroductionBtn = styled(Button)`
  position: relative;
  border: 0;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-image-slice: 1;
  z-index: 1;
  span {
    background: ${({ theme }) => theme.colors['bg-gradient']};
    border-image-slice: 1;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(100% - 2px * 2);
    height: calc(100% - 2px * 2);
    background: #fff;
    border-radius: calc(16px - 2px);
    z-index: -1;
  }
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
    box-shadow: none;
  }
`;

const LoginBtn = styled(Button)`
  position: relative;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-image-slice: 1;
  z-index: 1;
  color: #fff;
  flex-shrink: 0;
  box-shadow: none;
  border: 0;
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
    box-shadow: none;
    border: 0;
  }
`;

const StyledLink = styled(RoutingTabs)`
  height: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  & .en-tab-item {
    min-width: auto;
    padding: 0 46px;
  }
`;

const Profile = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-left: 10px;
  &.admin-profile {
    margin-left: 12px;
  }
`;

const StyledMenuItem = styled(MenuItem)`
  width: 198px;
  padding: 8px 4px 8px 11px;
  border-radius: 2px;
  overflow: visible;
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;

const DropdownOwner = styled.div`
  height: 100%;
  padding: 15px 0px;
  display: flex;
  align-items: center;
`;

const AdminConsoleLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 3px;
  ${fonts.Button4};
  font-weight: 500;
  white-space: nowrap;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.colors['bg-primary-lighter']};
    color: ${({ theme }) => theme.colors['text-primary-light']};

    svg path {
      fill: ${({ theme }) => theme.colors['ic-primary-light']};
    }
  }
`;

const PrivacyPolicyLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 3px;
  ${fonts.Button4};
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors['state-white-primary-hover']};
    color: ${({ theme }) => theme.colors['text-primary-light']};
  }
`;

const PrivacyPolicyBtn = styled(Button)`
  position: relative;
  border: 0;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  border-image-slice: 1;
  z-index: 1;
  span {
    background: ${({ theme }) => theme.colors['bg-gradient']};
    border-image-slice: 1;
    background-clip: text;
    -webkit-background-clip: text;
    color: transparent;
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 2px;
    left: 2px;
    width: calc(100% - 2px * 2);
    height: calc(100% - 2px * 2);
    background: #fff;
    border-radius: calc(16px - 2px);
    z-index: -1;
  }
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
    box-shadow: none;
  }
`;
